'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import WindowSize from '@/utils/windowSize';
import Container from '@/components/layout/Container';
import BlazeSlider from 'blaze-slider';
import 'blaze-slider/dist/blaze.css';
import {
  generateDescription,
  mergeArticles,
  ensureMinimumArticles
} from '@/lib/contentstack/article-format-utils';

export default function FeaturedArticleSlider({ block, position }) {
  const { articles } = block?.articles_slider || {};
  const { priorityArticles, latestArticles } = articles || {};
  const [showNav, setNav] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);
  const [mobileSlider, setMobileSlider] = useState('');
  const [desktopSlider, setDesktopSlider] = useState('');
  const [fontStyling, setFontStyling] = useState('');
  const [viewportChange, setViewportChange] = useState(false);
  const sliderRef = useRef();
  const [sliderArticlesDesc, setsliderArticlesDesc] = useState([]);
  const windowSize = WindowSize();

  const [sliderArticles, setSliderArticles] = useState([]);
  useEffect(() => {
    const formatSliderData = () => {
      const sliderArticles = mergeArticles(priorityArticles, latestArticles);
      ensureMinimumArticles(sliderArticles, latestArticles, 3);

      const descriptions = sliderArticles.map(generateDescription);
      setSliderArticles(sliderArticles);
      setsliderArticlesDesc(descriptions);
    };
    formatSliderData();
  }, [block, priorityArticles, latestArticles]);

  //detect window size to set which slider experience the user should be served
  useEffect(() => {
    if (windowSize.width <= 640) {
      // Mobile experience
      setNav('hidden');
      setMobileSlider(true);
      setDesktopSlider(false);
      setViewportChange(true);
    } else if (windowSize.width >= 1024) {
      // Desktop experience
      setNav('');
      setMobileSlider(false);
      setDesktopSlider(true);
      setViewportChange(true);
    } else if (typeof windowSize.width == 'undefined') {
      // Default experience where no viewport change has been triggered
      setNav('');
      setDesktopSlider(false);
      setMobileSlider(false);
      setViewportChange(false);
    } else {
      // Tablet experience
      setNav('');
      setDesktopSlider(false);
      setMobileSlider(false);
      setViewportChange(true);
    }

    if (windowSize.width > 1023) {
      setFontStyling('txt-btn txt-btn-primary');
    } else {
      setFontStyling('h6 underline');
    }
  }, [windowSize]);

  // State to store the initialized slider so that we may still move it w/ arrows that are outside of the blaze container
  const [blazeSlider, setBlazeSlider] = useState(null);

  useEffect(() => {
    if (!viewportChange) return;

    // Cleanup existing BlazeSlider instance
    if (blazeSlider) {
      blazeSlider.destroy();
      setBlazeSlider(null);
    }

    const sliderClass = mobileSlider
      ? '.featured-article-slider-mobile'
      : desktopSlider
        ? '.featured-article-slider-desktop'
        : '.featured-article-slider-tablet';

    const initializeSlider = (element) => {
      if (!element) return;

      const sliderInstance = new BlazeSlider(element, {
        all: {
          transitionDuration: 600
        }
      });

      // Ensure activeSlide updates correctly
      sliderInstance.onSlide((pageIndex) => {
        setActiveSlide(pageIndex);
      });

      setBlazeSlider(sliderInstance);
    };

    const el = document.querySelector(sliderClass);

    // If the slider element is not yet available, observe changes
    if (!el) {
      const observer = new MutationObserver(() => {
        const observedEl = document.querySelector(sliderClass);
        if (observedEl) {
          observer.disconnect();
          initializeSlider(observedEl);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return;
    }

    // Initialize slider if element is already available
    initializeSlider(el);

    // Cleanup on unmount
    return () => {
      if (blazeSlider) {
        blazeSlider.destroy();
        setBlazeSlider(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportChange]);

  return (
    <section
      id={`${block.articles_slider._metadata.uid}_${position}`}
      data-name="featured-article-slider"
      className="featured-article-slider relative"
    >
      {sliderArticles.length ? (
        <div>
          <Container>
            {mobileSlider ? (
              // mobile version of large slider
              <div className="featured-articles relative flex pb-8">
                <div
                  className="blaze-slider featured-article-slider-mobile col-span-2 container"
                  ref={sliderRef}
                >
                  <div className="blaze-container">
                    <div className="blaze-track-container">
                      <ul className="blaze-track">
                        {sliderArticles?.slice(0, 3).map((item, index) => {
                          return (
                            <li id={`slide-${index}`} key={index}>
                              <Link
                                href={item.url}
                                prefetch={false}
                                className="relative block h-67 w-full overflow-hidden"
                              >
                                <Image
                                  src={item.feature_image.url}
                                  alt={item.feature_image.title.replace(
                                    /\.[^/.]+$/,
                                    ''
                                  )}
                                  fill
                                  sizes="607px"
                                  className="object-cover"
                                  fetchPriority="high"
                                  loading="eager"
                                />
                              </Link>
                              <div className="px-4">
                                <h3 className="preheading-s sm:preheading-r text-parlor-green pt-4 pb-2 font-bold uppercase">
                                  {item.categoryConnection.title}
                                </h3>
                                <Link href={item.url} prefetch={false}>
                                  <h2 className="h4 sm:h3 mb-5">
                                    {item.title}
                                  </h2>
                                </Link>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                  {/* Mobile buttons */}
                  <div className="blaze-pagination indicators-dark absolute flex w-full" />
                </div>
              </div>
            ) : desktopSlider ? (
              //  desktop version of large slider
              <div className="featured-articles relative grid grid-cols-3 pb-15">
                <div
                  className="blaze-slider featured-article-slider-desktop col-span-2"
                  ref={sliderRef}
                >
                  <div className="blaze-container">
                    <div className="blaze-track-container">
                      <ul className="blaze-track">
                        {sliderArticles?.slice(0, 3).map((item, index) => {
                          return (
                            <li id={`slide-${index}`} key={index} className="">
                              <Link
                                href={sliderArticles[activeSlide].url}
                                title={item.title}
                                prefetch={false}
                                className="relative block h-134.5 w-full overflow-hidden"
                              >
                                <Image
                                  src={item.feature_image.url}
                                  alt={item.feature_image.title.replace(
                                    /\.[^/.]+$/,
                                    ''
                                  )}
                                  fill
                                  sizes="(min-width: 1536px) 789px, (min-width: 1024px) 715px, 715px"
                                  className="object-cover"
                                  fetchPriority="high"
                                  loading="eager"
                                />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                  {/* Desktop buttons */}
                  <div className="blaze-pagination indicators-light absolute bottom-19 col-span-2 flex w-2/3" />
                </div>

                <div className="relative pl-8">
                  <h3 className="preheading-s lg:preheading-r text-parlor-green py-6">
                    {sliderArticles[activeSlide].categoryConnection.title}
                  </h3>
                  <Link
                    href={sliderArticles[activeSlide].url}
                    className="h5 lg:h3 mb-6"
                    prefetch={false}
                  >
                    {sliderArticles[activeSlide].title}
                  </Link>
                  <p className="body-s lg:body-l h-50 truncate pt-6 pb-6 whitespace-normal">{`${sliderArticlesDesc[activeSlide]}`}</p>
                  <div className="pb-15 lg:pt-6">
                    <Link
                      href={sliderArticles[activeSlide].url}
                      className={fontStyling}
                      prefetch={false}
                    >
                      Read Article
                    </Link>
                  </div>

                  {/* Desktop arrows */}
                  <div className={`flex ${showNav} absolute bottom-0 pb-6`}>
                    <button
                      className={`blaze-prev min-h-3.5 min-w-2.75 bg-black`}
                      onClick={() => blazeSlider.prev()}
                    >
                      <span className="sr-only">previous slide</span>
                    </button>
                    <button
                      className={`blaze-next ml-4 min-h-3.5 min-w-2.75 bg-black`}
                      onClick={() => blazeSlider.next()}
                    >
                      <span className="sr-only">next slide</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // tablet version of large slider
              <div className="featured-articles relative grid grid-cols-3 pb-15">
                <div
                  className="blaze-slider featured-article-slider-tablet col-span-2"
                  ref={sliderRef}
                >
                  <div className="blaze-container">
                    <div className="blaze-track-container">
                      <ul className="blaze-track">
                        {sliderArticles?.slice(0, 3).map((item, index) => {
                          return (
                            <li id={`slide-${index}`} key={index} className="">
                              <Link
                                href={sliderArticles[activeSlide].url}
                                title={item.title}
                                prefetch={false}
                                className="relative block h-78 w-full overflow-hidden"
                              >
                                <Image
                                  src={item.feature_image.url}
                                  alt={item.feature_image.title.replace(
                                    /\.[^/.]+$/,
                                    ''
                                  )}
                                  fill
                                  sizes="(min-width: 640px) 458px, 458px"
                                  className="object-cover"
                                  fetchPriority="high"
                                />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="relative pl-8">
                  <h3 className="preheading-s lg:preheading-r text-parlor-green py-6">
                    {sliderArticles[activeSlide].categoryConnection.title}
                  </h3>
                  <Link
                    href={sliderArticles[activeSlide].url}
                    className="h5 lg:h3 mb-6"
                    prefetch={false}
                  >
                    {sliderArticles[activeSlide].title}
                  </Link>
                  <p className="body-s lg:body-l h-50 truncate pt-6 pb-6 whitespace-normal">{`${sliderArticlesDesc[activeSlide]}`}</p>
                  <div className="pb-15 lg:pt-6">
                    <Link
                      href={sliderArticles[activeSlide].url}
                      className={fontStyling}
                      prefetch={false}
                    >
                      Read Article
                    </Link>
                  </div>

                  {/* Tablet arrows */}
                  <div className={`flex ${showNav} absolute bottom-0 pb-6`}>
                    <button
                      className={`blaze-prev min-h-3.5 min-w-2.75 bg-black`}
                      onClick={() => blazeSlider.prev()}
                    >
                      <span className="sr-only">previous slide</span>
                    </button>
                    <button
                      className={`blaze-next ml-4 min-h-3.5 min-w-2.75 bg-black`}
                      onClick={() => blazeSlider.next()}
                    >
                      <span className="sr-only">next slide</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Container>
        </div>
      ) : (
        ''
      )}
    </section>
  );
}
